import { Area, AreaChart, Bar, BarChart, CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '../../components/ui/chart';
import { cn } from '../../lib/utils';
import { formatReportingAmount } from '../../lib/reportingMoney';
import { formatBucketLabel } from '../reporting/chartDate';
import type { ChartStyle } from '../reporting/chartPrefs';
import type { BusinessSeriesBase } from '../../lib/businessRevenueSeries';

// The Business-lens historic time-series chart: a CONTINUOUS monthly domain with
// every dimension value as its own palette-coloured series (bar = STACKED
// composition per month, line/area = per-series over time). A clickable legend
// toggles each series' visibility WITHOUT recomputing the domain — hidden series
// are just not rendered; `result.months` / `result.rows` are untouched. Series
// colours are set directly on each element (not via ChartContainer's --color-<key>
// vars, which can't hold dimension labels with spaces — the Am×brand precedent).
//
// MONEY vs COUNT mode: pass a currency for money (rows carry cents → scaled to $
// and currency-formatted), or `null` for account counts (rows carry integers →
// rendered as-is, no currency, no scaling).
//
// Axes/grid/tooltip are single DIRECT children and the series are a mapped ARRAY
// (Recharts traverses arrays but NOT fragments — the charts-v2 gotcha).

const dollars = (cents: number) => cents / 100;

export function BusinessTimeSeriesChart({
  result,
  style,
  currency,
  hidden,
  onToggleSeries,
}: {
  result: BusinessSeriesBase;
  style: ChartStyle;
  currency: string | null; // null → count mode (integers, no currency)
  hidden: Set<string>;
  onToggleSeries: (key: string) => void;
}) {
  const isCount = currency === null;
  // Money rows carry cents (→ dollars); count rows carry integers (as-is).
  const scale = (v: number) => (isCount ? v : dollars(v));
  const fmt = (v: number) =>
    isCount ? Math.round(v).toLocaleString('en-US') : formatReportingAmount(v, currency, 0);

  const visible = result.series.filter((s) => !hidden.has(s.key));
  // Labels only (no color → no invalid --color-<key> var for spaced labels).
  const config: ChartConfig = Object.fromEntries(result.series.map((s) => [s.key, { label: s.label }]));

  const data = result.rows.map((r) => {
    const row: Record<string, number | string> = { month: r.month };
    for (const s of result.series) row[s.key] = scale(Number(r[s.key] ?? 0));
    return row;
  });

  const common = { data, margin: { left: 8, right: 12, top: 8, bottom: 0 }, accessibilityLayer: true };
  const grid = <CartesianGrid vertical={false} strokeDasharray="3 3" />;
  const xAxis = (
    <XAxis
      dataKey="month"
      tickLine={false}
      axisLine={false}
      tickMargin={8}
      minTickGap={16}
      tick={{ fontSize: 11 }}
      tickFormatter={(m) => formatBucketLabel(String(m), 'monthly')}
    />
  );
  const yAxis = (
    <YAxis tickLine={false} axisLine={false} width={64} tick={{ fontSize: 11 }} tickFormatter={(v) => fmt(Number(v))} />
  );
  const tooltip = (
    <ChartTooltip
      cursor={{ fill: 'var(--color-muted)', opacity: 0.4 }}
      content={
        <ChartTooltipContent
          labelFormatter={(m) => formatBucketLabel(String(m), 'monthly')}
          formatter={(value, name, item) => (
            <div className="flex w-full items-center gap-2">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                style={{ backgroundColor: item?.color as string }}
                aria-hidden
              />
              <span className="flex-1 text-muted-foreground">{String(name)}</span>
              <span className="font-mono font-medium tabular-nums text-foreground">{fmt(Number(value))}</span>
            </div>
          )}
        />
      }
    />
  );

  return (
    <div>
      <ChartContainer config={config} className="aspect-auto h-80 w-full">
        {style === 'line' ? (
          <LineChart {...common}>
            {grid}
            {xAxis}
            {yAxis}
            {tooltip}
            {visible.map((s) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stroke={s.color}
                dot={false}
                strokeWidth={2}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        ) : style === 'area' ? (
          <AreaChart {...common}>
            {grid}
            {xAxis}
            {yAxis}
            {tooltip}
            {visible.map((s) => (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stroke={s.color}
                fill={s.color}
                fillOpacity={0.2}
                strokeWidth={1.5}
                stackId="rev"
                isAnimationActive={false}
              />
            ))}
          </AreaChart>
        ) : (
          <BarChart {...common}>
            {grid}
            {xAxis}
            {yAxis}
            {tooltip}
            {visible.map((s) => (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.label}
                fill={s.color}
                stackId="rev"
                maxBarSize={48}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        )}
      </ChartContainer>

      {/* Clickable legend — click a chip to show/hide its series. Purely visual;
          it never re-pivots (the domain is fixed). */}
      <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1.5">
        {result.series.map((s) => {
          const off = hidden.has(s.key);
          return (
            <button
              key={s.key}
              type="button"
              aria-pressed={!off}
              onClick={() => onToggleSeries(s.key)}
              title={`${s.label} — ${fmt(scale(s.totalCents))}`}
              className={cn(
                'flex items-center gap-1.5 rounded px-1.5 py-0.5 text-xs transition-colors hover:bg-slate-50',
                off && 'opacity-40',
              )}
            >
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                style={{ backgroundColor: s.color }}
                aria-hidden
              />
              <span className={cn('text-slate-600', off && 'line-through')}>{s.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
